import { useQuery } from '@tanstack/react-query'
import { fetchHealth } from '@/lib/api'
import type { ServiceHealth } from '@/lib/api'
import { ApiError } from '@/lib/api'
import { useApiContext } from './useApiContext'

export type ConnectionStatus = 'checking' | 'online' | 'offline'

export interface HealthState {
  status: ConnectionStatus
  health: ServiceHealth | undefined
  error: ApiError | undefined
  refetch: () => void
  isFetching: boolean
}

const POLL_INTERVAL_MS = 30_000

export function useHealth(): HealthState {
  const context = useApiContext()

  const query = useQuery({
    queryKey: ['health', context.baseUrl],
    queryFn: ({ signal }) => fetchHealth(context, signal),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    // A health probe that failed is information, not something to hammer.
    retry: 1,
    staleTime: 10_000,
  })

  const status: ConnectionStatus = query.isSuccess
    ? 'online'
    : query.isError
      ? 'offline'
      : 'checking'

  return {
    status,
    health: query.data,
    error: query.error instanceof ApiError ? query.error : undefined,
    refetch: () => void query.refetch(),
    isFetching: query.isFetching,
  }
}
